import React, { useEffect, useMemo, useRef, useState } from 'react';
import Tree, { CustomNodeElementProps, RawNodeDatum } from 'react-d3-tree';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  FiActivity,
  FiArrowRight,
  FiCalendar,
  FiCheck,
  FiChevronRight,
  FiCopy,
  FiCreditCard,
  FiDollarSign,
  FiExternalLink,
  FiGlobe,
  FiGitBranch,
  FiLink,
  FiMove,
  FiShield,
  FiShare2,
  FiTrendingUp,
  FiUser,
  FiUsers,
  FiX,
  FiZoomIn,
} from 'react-icons/fi';
import { useLocation } from 'react-router-dom';
import type { AffiliatePayoutDetailsInput } from '../../api';
import { useAuthStore } from '../../stores';
import { useApi } from '../../hooks/useApi';
import { showToast } from '../../utils/toast';

const formatMoney = (cents: number, currency = 'usd') =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: String(currency || 'usd').toUpperCase(),
  }).format((Number(cents) || 0) / 100);

type Period = '1W' | '1M' | '3M' | '1Y' | 'ALL';
type GrowthPeriod = '7D' | '30D' | '90D' | '12M';

const periodDays: Record<Period, number> = {
  '1W': 7,
  '1M': 30,
  '3M': 90,
  '1Y': 365,
  ALL: Number.POSITIVE_INFINITY,
};

const growthPeriodDays: Record<GrowthPeriod, number> = {
  '7D': 7,
  '30D': 30,
  '90D': 90,
  '12M': 365,
};

const chartMoney = (value: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);

const ReferralNode = ({ nodeDatum }: CustomNodeElementProps) => {
  const isRoot = nodeDatum.attributes?.isRoot === true;
  const plan = String(nodeDatum.attributes?.plan || 'none').toLowerCase();
  const subtitle = String(nodeDatum.attributes?.subtitle || '');
  const initial = nodeDatum.name.trim().charAt(0).toUpperCase() || 'U';

  if (isRoot) {
    return (
      <foreignObject x={-210} y={-140} width={420} height={150}>
        <div className="relative h-[140px] overflow-hidden rounded-[28px] border border-blue-500/50 bg-[radial-gradient(circle_at_22%_45%,rgba(37,99,235,0.2),transparent_30%),linear-gradient(120deg,#071b43,#06132f)] px-8 shadow-[0_18px_50px_rgba(3,15,38,0.42)]">
          <div className="pointer-events-none absolute -bottom-16 -right-8 h-40 w-40 rounded-full border border-blue-500/20" />
          <div className="pointer-events-none absolute -bottom-20 -right-3 h-40 w-40 rounded-full border border-blue-500/15" />
          <div className="flex h-full items-center gap-6">
            <div className="flex h-[82px] w-[82px] shrink-0 items-center justify-center rounded-full border border-blue-400/70 bg-gradient-to-br from-blue-500/40 to-blue-900/60 text-white shadow-[0_0_0_6px_rgba(37,99,235,0.16),0_0_30px_rgba(37,99,235,0.36)]">
              <FiUser className="h-10 w-10" />
            </div>
            <div className="min-w-0">
              <p className="truncate text-2xl font-bold tracking-tight text-white">{nodeDatum.name}</p>
              <p className="mt-2 truncate text-base text-blue-200/65">{subtitle}</p>
            </div>
            <div className="absolute right-7 top-6 flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-blue-400 to-blue-700 text-white shadow-lg shadow-blue-500/20">
              <FiShield className="h-5 w-5" />
            </div>
          </div>
        </div>
      </foreignObject>
    );
  }

  return (
    <foreignObject x={-190} y={8} width={380} height={160}>
      <div className="relative pt-3">
        <span className="absolute left-1/2 top-0 z-10 h-3 w-3 -translate-x-1/2 rounded-full border-2 border-[#07142e] bg-blue-500 shadow-[0_0_12px_rgba(37,99,235,0.9)]" />
        <div className="relative h-[138px] overflow-hidden rounded-[26px] border border-blue-500/25 border-l-2 border-l-blue-500 bg-[linear-gradient(135deg,#0a1834,#0c1428)] px-6 shadow-[0_18px_44px_rgba(3,10,24,0.35)]">
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-blue-500/[0.05] to-transparent" />
          <div className="relative flex h-full items-center gap-5">
            <div className="relative flex h-[72px] w-[72px] shrink-0 items-center justify-center rounded-2xl border border-blue-500/40 bg-gradient-to-br from-blue-600/35 to-blue-950 text-3xl font-semibold text-white shadow-inner">
              {initial}
              <span className="absolute right-2 top-2 h-3 w-3 rounded-full border-2 border-[#0a1834] bg-blue-400" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-lg font-bold text-white">{nodeDatum.name}</p>
              <p className="mt-2 flex items-center gap-2 truncate text-sm text-blue-200/60">
                <FiCalendar className="h-4 w-4 shrink-0" />
                {subtitle}
              </p>
              <span
                className="mt-3 inline-flex rounded-xl border border-blue-500/60 bg-blue-500/10 px-3 py-1 text-xs font-semibold capitalize text-blue-400"
              >
                {plan === 'none' ? 'No Plan' : plan}
              </span>
            </div>
            <FiChevronRight className="h-6 w-6 shrink-0 text-blue-500" />
          </div>
        </div>
      </div>
    </foreignObject>
  );
};

const AffiliateProgram: React.FC = () => {
  const location = useLocation();
  const user = useAuthStore((state) => state.user);
  const referralsQuery = useApi.useAffiliateReferrals();
  const payoutDetailsQuery = useApi.useAffiliatePayoutDetails();
  const referrals = useMemo(
    () => referralsQuery.data?.referrals || [],
    [referralsQuery.data?.referrals]
  );
  const referralId = String(user?._id || user?.id || '').trim();
  const referralCode = referralId ? referralId.slice(-10).toUpperCase() : 'AFFILIATE';
  const referralLink = referralId
    ? `${window.location.origin}/register?ref=${encodeURIComponent(referralId)}`
    : `${window.location.origin}/register`;
  const [copied, setCopied] = useState(false);
  const [showAllHistory, setShowAllHistory] = useState(false);
  const [period, setPeriod] = useState<Period>('3M');
  const [growthPeriod, setGrowthPeriod] = useState<GrowthPeriod>('12M');
  const [showRates, setShowRates] = useState(false);
  const [showPayoutDetails, setShowPayoutDetails] = useState(false);
  const [payoutForm, setPayoutForm] = useState<AffiliatePayoutDetailsInput>({
    details: '',
  });
  const treeContainerRef = useRef<HTMLDivElement>(null);
  const [treeTranslate, setTreeTranslate] = useState({ x: 480, y: 170 });

  const savePayoutDetailsMutation = useApi.useSaveAffiliatePayoutDetails({
    onSuccess: (response) => {
      showToast('success', response.message || 'Payout details saved securely');
      setShowPayoutDetails(false);
    },
    onError: (error: any) => {
      showToast('error', error?.response?.data?.error || error.message || 'Failed to save payout details');
    },
  });

  const openPayoutDetails = () => {
    const saved = payoutDetailsQuery.data?.payoutDetails;
    setPayoutForm({
      details: saved?.details || '',
    });
    setShowPayoutDetails(true);
  };

  const savePayoutDetails = (event: React.FormEvent) => {
    event.preventDefault();
    savePayoutDetailsMutation.mutate(payoutForm);
  };

  const referralTreeData = useMemo<RawNodeDatum>(
    () => ({
      name: user?.name || 'You',
      attributes: {
        subtitle: 'Your affiliate account',
        isRoot: true,
      },
      children: referrals.map((referral) => ({
        name: referral.name,
        attributes: {
          subtitle: `Joined ${new Date(referral.joinedAt).toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}`,
          plan: referral.plan || 'none',
          isRoot: false,
        },
      })),
    }),
    [referrals, user?.name],
  );

  useEffect(() => {
    const container = treeContainerRef.current;
    if (!container) return;

    const centerTree = () => {
      setTreeTranslate({ x: container.clientWidth / 2, y: 170 });
    };
    centerTree();

    const observer = new ResizeObserver(centerTree);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const copyReferralLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  const shareReferralLink = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Join TradePilot AI',
          text: 'Join me on TradePilot AI.',
          url: referralLink,
        });
        return;
      }
      await copyReferralLink();
    } catch {
      // Closing the native share dialog is not an application error.
    }
  };

  const summary = referralsQuery.data?.summary;
  const totalBusinessCents = useMemo(
    () => (referralsQuery.data?.commissions || []).reduce(
      (total, commission) => total + (Number(commission.grossAmountCents) || 0),
      0,
    ),
    [referralsQuery.data?.commissions],
  );
  const referralPerformance = useMemo(() => {
    const rows = new Map<string, {
      name: string;
      businessCents: number;
      commissionCents: number;
    }>();

    referrals.forEach((referral) => {
      rows.set(referral.name, {
        name: referral.name,
        businessCents: 0,
        commissionCents: 0,
      });
    });

    (referralsQuery.data?.commissions || []).forEach((commission) => {
      const current = rows.get(commission.referredUserName) || {
        name: commission.referredUserName,
        businessCents: 0,
        commissionCents: 0,
      };
      current.businessCents += Number(commission.grossAmountCents) || 0;
      current.commissionCents += Number(commission.commissionAmountCents) || 0;
      rows.set(commission.referredUserName, current);
    });

    return Array.from(rows.values())
      .sort((left, right) => right.commissionCents - left.commissionCents);
  }, [referrals, referralsQuery.data?.commissions]);
  const topReferrals = referralPerformance.slice(0, 10);

  const payoutHistory = useMemo(() => {
    const batches = new Map<string, {
      id: string;
      paidAt: string;
      amountCents: number;
      currency: string;
      commissionCount: number;
      referralNames: Set<string>;
    }>();

    (referralsQuery.data?.commissions || [])
      .filter((commission) => commission.status === 'paid')
      .forEach((commission) => {
        const paidAt = commission.paidAt || commission.createdAt;
        const key = commission.paidAt || commission.id;
        const current = batches.get(key) || {
          id: key,
          paidAt,
          amountCents: 0,
          currency: commission.currency || 'usd',
          commissionCount: 0,
          referralNames: new Set<string>(),
        };
        current.amountCents += Number(commission.commissionAmountCents) || 0;
        current.commissionCount += 1;
        current.referralNames.add(commission.referredUserName);
        batches.set(key, current);
      });

    return Array.from(batches.values())
      .map((batch) => ({
        ...batch,
        referralSummary: Array.from(batch.referralNames).join(', '),
      }))
      .sort((left, right) => new Date(right.paidAt).getTime() - new Date(left.paidAt).getTime());
  }, [referralsQuery.data?.commissions]);

  const referralGrowthData = useMemo(() => {
    const now = new Date();
    const isYear = growthPeriod === '12M';
    const bucketCount = isYear ? 12 : growthPeriod === '7D' ? 7 : 10;
    const totalDays = growthPeriodDays[growthPeriod];

    return Array.from({ length: bucketCount }, (_, index) => {
      let end: Date;
      let label: string;

      if (isYear) {
        end = new Date(now.getFullYear(), now.getMonth() - (bucketCount - index - 1) + 1, 0, 23, 59, 59, 999);
        label = end.toLocaleDateString(undefined, { month: 'short' });
      } else {
        const daysFromNow = Math.max(
          0,
          Math.round(totalDays - ((index + 1) * totalDays) / bucketCount),
        );
        end = new Date(now.getTime() - daysFromNow * 86400000);
        end.setHours(23, 59, 59, 999);
        label = end.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      }

      return {
        label,
        referrals: referrals.filter((referral) => new Date(referral.joinedAt) <= end).length,
      };
    });
  }, [growthPeriod, referrals]);
  const commissions = useMemo(
    () =>
      (referralsQuery.data?.commissions || []).map((commission) => {
        const eventDate = commission.occurredAt || commission.createdAt;
        return {
          id: commission.id,
          name: commission.referredUserName,
          action: commission.description || `${commission.referredUserPlan} plan payment`,
          amount: formatMoney(commission.grossAmountCents, commission.currency),
          commission: formatMoney(commission.commissionAmountCents, commission.currency),
          date: new Date(eventDate).toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          }),
          status: commission.status === 'paid' ? 'Paid' : 'Succeeded',
        };
      }),
    [referralsQuery.data?.commissions],
  );
  const visibleCommissions = showAllHistory ? commissions : commissions.slice(0, 5);

  const performanceData = useMemo(() => {
    const source = referralsQuery.data?.commissions || [];
    const eventTime = (item: (typeof source)[number]) =>
      new Date(item.occurredAt || item.createdAt).getTime();

    const now = new Date();
    const endOfToday = new Date(now);
    endOfToday.setHours(23, 59, 59, 999);

    const rangeDays = Number.isFinite(periodDays[period])
      ? periodDays[period]
      : Math.max(
          30,
          Math.ceil(
            (endOfToday.getTime() -
              Math.min(...source.map(eventTime), endOfToday.getTime())) /
              86400000,
          ),
        );

    const cutoff = endOfToday.getTime() - rangeDays * 86400000;
    const filtered = source.filter((item) => eventTime(item) >= cutoff);

    // Daily buckets for short ranges; weekly for 3M; monthly for 1Y/ALL
    const useDaily = period === '1W' || period === '1M';
    const useMonthly = period === '1Y' || period === 'ALL';
    const bucketCount = useDaily
      ? Math.min(rangeDays, period === '1W' ? 7 : 30)
      : useMonthly
        ? period === '1Y'
          ? 12
          : Math.min(24, Math.max(6, Math.ceil(rangeDays / 30)))
        : Math.ceil(rangeDays / 7);

    const startOfDay = (date: Date) => {
      const d = new Date(date);
      d.setHours(0, 0, 0, 0);
      return d;
    };

    const buckets = Array.from({ length: bucketCount }, (_, index) => {
      let start: Date;
      let end: Date;
      let label: string;

      if (useMonthly) {
        start = new Date(now.getFullYear(), now.getMonth() - (bucketCount - index - 1), 1);
        end = new Date(now.getFullYear(), now.getMonth() - (bucketCount - index - 1) + 1, 0, 23, 59, 59, 999);
        label = start.toLocaleDateString(undefined, {
          month: 'short',
          ...(period === 'ALL' ? { year: '2-digit' as const } : {}),
        });
      } else if (useDaily) {
        start = startOfDay(
          new Date(endOfToday.getTime() - (bucketCount - index - 1) * 86400000),
        );
        end = new Date(start);
        end.setHours(23, 59, 59, 999);
        label = start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      } else {
        // Weekly: label by week START so sales don't appear as "today"
        end = new Date(endOfToday.getTime() - (bucketCount - index - 1) * 7 * 86400000);
        end.setHours(23, 59, 59, 999);
        start = startOfDay(new Date(end.getTime() - 6 * 86400000));
        label = start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      }

      return {
        label,
        start: start.getTime(),
        end: end.getTime(),
        business: 0,
        commission: 0,
      };
    });

    filtered.forEach((item) => {
      const timestamp = eventTime(item);
      const bucket = buckets.find((entry) => timestamp >= entry.start && timestamp <= entry.end);
      if (bucket) {
        bucket.business += (Number(item.grossAmountCents) || 0) / 100;
        bucket.commission += (Number(item.commissionAmountCents) || 0) / 100;
      }
    });

    return buckets;
  }, [period, referralsQuery.data?.commissions]);

  const displayedStats = [
    {
      label: 'Total Referrals',
      value: String(summary?.referralCount ?? referrals.length),
      icon: FiUsers,
    },
    {
      label: 'Total Business',
      value: formatMoney(totalBusinessCents),
      icon: FiDollarSign,
    },
    {
      label: 'Commissions Earned',
      value: formatMoney(summary?.totalEarnedCents || 0),
      icon: FiTrendingUp,
    },
    // {
    //   label: 'Total Volume (Lots)',
    //   value: '0.00',
    //   icon: FiActivity,
    // },
  ];

  const payoutDetailsModal = showPayoutDetails ? (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-2xl border border-border bg-surface-elevated shadow-2xl">
        <div className="flex items-start justify-between border-b border-border p-5">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <FiCreditCard className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-foreground">
                {payoutDetailsQuery.data?.hasPayoutDetails ? 'Update Payout Details' : 'Add Payout Details'}
              </h2>
              <p className="mt-1 text-xs text-muted">
                Enter your bank account or PayPal details for approved payouts.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowPayoutDetails(false)}
            className="rounded-lg p-2 text-muted transition hover:bg-surface hover:text-foreground"
            aria-label="Close payout details"
          >
            <FiX className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={savePayoutDetails} className="space-y-4 p-5">
          {payoutDetailsQuery.data?.hasPayoutDetails && (
            <div className="rounded-xl border border-primary/20 bg-primary/10 px-4 py-3 text-xs text-primary">
              Saved details: {payoutDetailsQuery.data.payoutDetails?.detailsPreview || 'on file'}. Edit the text below to update.
            </div>
          )}

          <label className="space-y-1.5 text-xs font-semibold text-foreground">
            Bank or PayPal details
            <textarea
              required
              minLength={5}
              maxLength={2000}
              rows={7}
              value={payoutForm.details}
              onChange={(event) => setPayoutForm({ details: event.target.value })}
              placeholder={`Example:\nPayPal: name@example.com\n\nor\n\nBank name: ...\nAccount holder: ...\nAccount / IBAN: ...\nRouting / SWIFT: ...`}
              className="w-full resize-y rounded-xl border border-border bg-background px-3 py-2.5 text-sm font-normal leading-6 text-foreground outline-none transition focus:border-primary"
            />
          </label>

          <div className="flex items-start gap-2 rounded-xl bg-surface px-4 py-3 text-xs leading-5 text-muted">
            <FiShield className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            Your payout details are encrypted before storage and are only available to authenticated administrators processing payouts.
          </div>

          <div className="flex justify-end gap-3 border-t border-border pt-4">
            <button
              type="button"
              onClick={() => setShowPayoutDetails(false)}
              className="rounded-lg border border-border px-4 py-2.5 text-xs font-semibold text-foreground hover:bg-surface"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={savePayoutDetailsMutation.isPending}
              className="rounded-lg bg-primary px-5 py-2.5 text-xs font-semibold text-white shadow-sm transition hover:brightness-110 disabled:opacity-50"
            >
              {savePayoutDetailsMutation.isPending ? 'Saving securely…' : 'Save Payout Details'}
            </button>
          </div>
        </form>
      </div>
    </div>
  ) : null;

  const referralTreeSection = (
    <section id="affiliate-referrals" className="h-full min-w-0 overflow-hidden rounded-2xl border border-border bg-surface-elevated shadow-md">
      <div className="flex flex-col gap-3 border-b border-border px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <FiGitBranch className="h-4 w-4" />
          </div>
          <div>
            <h2 className="text-sm font-bold text-foreground">Referral Tree</h2>
            <p className="mt-0.5 text-[11px] text-muted">Your direct affiliate network</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface px-3 py-1.5 text-[10px] font-medium text-muted">
            <FiMove className="h-3 w-3" />
            Drag to move
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface px-3 py-1.5 text-[10px] font-medium text-muted">
            <FiZoomIn className="h-3 w-3" />
            Scroll to zoom
          </span>
          <span className="rounded-full bg-primary/10 px-3 py-1.5 text-[10px] font-bold text-primary">
            {referrals.length} referrals
          </span>
        </div>
      </div>

      <div
        ref={treeContainerRef}
        className="relative h-[calc(100vh-14rem)] min-h-[480px] w-full cursor-grab overflow-hidden bg-[radial-gradient(circle_at_center,hsl(var(--border))_1px,transparent_1px)] [background-size:22px_22px] active:cursor-grabbing"
      >
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-primary/[0.025] via-transparent to-surface-elevated/40" />
        {referralsQuery.isLoading ? (
          <div className="flex h-full items-center justify-center text-sm text-muted">Loading referral tree…</div>
        ) : referralsQuery.isError ? (
          <div className="flex h-full items-center justify-center text-sm text-red-500">
            Unable to load your referral tree.
          </div>
        ) : (
          <>
            <Tree
              data={referralTreeData}
              translate={treeTranslate}
              orientation="vertical"
              pathFunc="step"
              pathClassFunc={() => 'affiliate-tree-link'}
              renderCustomNodeElement={(props) => <ReferralNode {...props} />}
              nodeSize={{ x: 410, y: 225 }}
              separation={{ siblings: 1.05, nonSiblings: 1.2 }}
              zoom={referrals.length > 5 ? 0.55 : 0.72}
              scaleExtent={{ min: 0.35, max: 1.5 }}
              draggable
              zoomable
              collapsible={false}
              transitionDuration={250}
            />
            {referrals.length === 0 && (
              <div className="pointer-events-none absolute bottom-8 left-1/2 -translate-x-1/2 rounded-full border border-border bg-surface-elevated/95 px-4 py-2 text-xs text-muted shadow-lg">
                Share your referral link to grow your network
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );

  const referralTableSection = (
    <section className="h-full min-w-0 overflow-hidden rounded-2xl border border-border bg-surface-elevated shadow-md">
      <div className="flex flex-col gap-3 border-b border-border px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-base font-bold text-foreground">Referral List</h2>
          <p className="mt-1 text-xs text-muted">The same users shown in the tree, with their plan and earnings.</p>
        </div>
        <span className="rounded-full bg-primary/10 px-3 py-1 text-[10px] font-bold text-primary">
          {referrals.length} users
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] text-left text-xs">
          <thead className="bg-surface text-muted">
            <tr>
              <th className="px-5 py-3 font-semibold">#</th>
              <th className="px-5 py-3 font-semibold">User</th>
              <th className="px-5 py-3 font-semibold">Level</th>
              <th className="px-5 py-3 font-semibold">Plan</th>
              <th className="px-5 py-3 font-semibold">Joined</th>
              <th className="px-5 py-3 text-right font-semibold">Business</th>
              <th className="px-5 py-3 text-right font-semibold">Commission</th>
              <th className="px-5 py-3 text-right font-semibold">Status</th>
            </tr>
          </thead>
          <tbody>
            {referrals.map((referral, index) => {
              const performance = referralPerformance.find((row) => row.name === referral.name);
              return (
                <tr key={referral.id} className="border-t border-border transition hover:bg-surface/70">
                  <td className="px-5 py-4 text-muted">{index + 1}</td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-primary/20 bg-primary/10 text-sm font-bold text-primary">
                        {referral.name.trim().charAt(0).toUpperCase() || 'U'}
                      </div>
                      <span className="font-semibold text-foreground">{referral.name}</span>
                    </div>
                  </td>
                  <td className="px-5 py-4 text-muted">L1</td>
                  <td className="px-5 py-4">
                    <span className="rounded-full border border-primary/20 bg-primary/10 px-2.5 py-1 font-semibold capitalize text-primary">
                      {referral.plan === 'none' ? 'No plan' : referral.plan}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-muted">
                    {new Date(referral.joinedAt).toLocaleDateString(undefined, {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </td>
                  <td className="px-5 py-4 text-right tabular-nums text-foreground">
                    {formatMoney(performance?.businessCents || 0)}
                  </td>
                  <td className="px-5 py-4 text-right font-bold tabular-nums text-primary">
                    {formatMoney(performance?.commissionCents || 0)}
                  </td>
                  <td className="px-5 py-4 text-right">
                    <span className="rounded-full bg-primary/10 px-2.5 py-1 font-semibold capitalize text-primary">
                      {referral.status}
                    </span>
                  </td>
                </tr>
              );
            })}
            {!referralsQuery.isLoading && referrals.length === 0 && (
              <tr>
                <td colSpan={8} className="px-5 py-14 text-center text-muted">
                  No referrals yet. Share your link to add users to the tree and table.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );

  const commissionHistorySection = (
    <section id="affiliate-commissions" className="scroll-mt-24 rounded-2xl border border-border bg-surface-elevated p-5 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-sm font-bold text-foreground">Commission History</h2>
          <p className="mt-1 text-xs text-muted">Every eligible payment generated by your referrals.</p>
        </div>
        <span className="rounded-full bg-primary/10 px-3 py-1 text-[10px] font-bold text-primary">
          {commissions.length} transactions
        </span>
      </div>
      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full min-w-[760px] text-left text-xs">
          <thead className="bg-surface">
            <tr className="border-b border-border text-muted">
              {['From', 'Payment Action', 'Amount', 'Your Commission', 'Date', 'Status'].map((heading) => (
                <th key={heading} className="px-4 py-3 font-semibold">{heading}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleCommissions.map((row) => (
              <tr key={row.id} className="border-b border-border last:border-0 hover:bg-surface/70">
                <td className="px-4 py-3 font-medium text-foreground">{row.name}</td>
                <td className="max-w-[240px] truncate px-4 py-3 text-muted">{row.action}</td>
                <td className="px-4 py-3 tabular-nums text-foreground">{row.amount}</td>
                <td className="px-4 py-3 font-semibold tabular-nums text-primary">{row.commission}</td>
                <td className="px-4 py-3 text-muted">{row.date}</td>
                <td className="px-4 py-3">
                  <span
                    className={`rounded-full px-2.5 py-1 text-[10px] font-semibold ${
                      row.status === 'Paid'
                        ? 'bg-primary/10 text-primary'
                        : 'bg-sky-500/10 text-sky-600 dark:text-sky-400'
                    }`}
                  >
                    {row.status}
                  </span>
                </td>
              </tr>
            ))}
            {visibleCommissions.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-muted">
                  No commission history yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {commissions.length > 5 && (
        <button
          type="button"
          onClick={() => setShowAllHistory((current) => !current)}
          className="mx-auto mt-4 inline-flex w-full items-center justify-center gap-1.5 text-xs font-semibold text-primary hover:underline"
        >
          {showAllHistory ? 'Show recent history' : `View all history (${commissions.length})`}
          <FiArrowRight className={`h-3.5 w-3.5 transition-transform ${showAllHistory ? 'rotate-90' : ''}`} />
        </button>
      )}
    </section>
  );

  if (location.hash === '#affiliate-referrals') {
    return (
      <div className="animate-fadeIn space-y-5 pb-8">
        <header>
          <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-primary">
            <FiUsers className="h-3 w-3" />
            Referrals
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Your Referral Network</h1>
          <p className="mt-1 text-sm text-muted">Explore everyone who joined through your affiliate link.</p>
        </header>
        <div className="grid items-stretch gap-5 xl:grid-cols-2">
          {referralTreeSection}
          {referralTableSection}
        </div>
      </div>
    );
  }

  if (location.hash === '#affiliate-links') {
    return (
      <div className="animate-fadeIn space-y-5 pb-8">
        <header>
          <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-primary">
            <FiLink className="h-3 w-3" />
            Partner links
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Partner Links</h1>
          <p className="mt-1 text-sm text-muted">Share your unique link and grow your referral network.</p>
        </header>

        <section className="relative overflow-hidden rounded-2xl border border-primary/20 bg-gradient-to-r from-primary/[0.12] via-primary/[0.05] to-transparent p-5 shadow-sm sm:p-6">
          <div className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-primary/10 blur-3xl" />
          <div className="relative">
            <div className="flex items-center gap-2 text-[10px] font-semibold text-primary">
              <span className="rounded-full bg-primary/10 px-2.5 py-1">New</span>
              Partner landing page
            </div>
            <h2 className="mt-3 text-xl font-bold text-foreground">Trade Pilot Landing Page</h2>
            <p className="mt-1 max-w-4xl text-xs leading-5 text-muted">
              Send prospects to your dedicated registration page. Every signup is automatically tagged with your referral code.
            </p>
            <div className="mt-5 flex flex-col gap-3 lg:flex-row">
              <div className="flex min-w-0 flex-1 items-center gap-2 rounded-xl border border-border bg-surface-elevated/80 px-4 py-3">
                <FiGlobe className="h-4 w-4 shrink-0 text-primary" />
                <span className="min-w-0 flex-1 truncate font-mono text-xs text-foreground">{referralLink}</span>
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={copyReferralLink} className="inline-flex items-center gap-2 rounded-xl border border-border bg-surface-elevated px-4 py-3 text-xs font-semibold text-foreground transition hover:border-primary hover:text-primary">
                  {copied ? <FiCheck className="h-4 w-4" /> : <FiCopy className="h-4 w-4" />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
                <button type="button" onClick={shareReferralLink} className="inline-flex items-center gap-2 rounded-xl border border-border bg-surface-elevated px-4 py-3 text-xs font-semibold text-foreground transition hover:border-primary hover:text-primary">
                  <FiShare2 className="h-4 w-4" />
                  Share
                </button>
                <button type="button" onClick={() => window.open(referralLink, '_blank', 'noopener,noreferrer')} className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-3 text-xs font-semibold text-white shadow-lg shadow-primary/20 transition hover:brightness-110">
                  Preview
                  <FiExternalLink className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </section>

        <section className="grid gap-5 xl:grid-cols-[minmax(0,2fr)_minmax(280px,1fr)]">
          <article className="rounded-2xl border border-border bg-surface-elevated p-5 shadow-sm">
            <h2 className="text-base font-bold text-foreground">Referral Link</h2>
            <p className="mt-2 max-w-3xl text-xs leading-5 text-muted">
              Share this link anywhere you promote Trade Pilot. New accounts created through it will appear in your referral tree.
            </p>
            <div className="mt-5 rounded-xl border border-border bg-surface p-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <span className="rounded-full bg-primary/10 px-3 py-1 text-[10px] font-bold text-primary">{referralCode}</span>
                <div className="flex gap-2">
                  <button type="button" onClick={shareReferralLink} className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-[10px] font-semibold text-foreground hover:text-primary">
                    <FiShare2 className="h-3.5 w-3.5" /> Share
                  </button>
                  <button type="button" onClick={copyReferralLink} className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-[10px] font-semibold text-foreground hover:text-primary">
                    <FiCopy className="h-3.5 w-3.5" /> Copy
                  </button>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2 rounded-lg bg-surface-elevated px-3 py-3">
                <FiLink className="h-4 w-4 shrink-0 text-primary" />
                <span className="min-w-0 flex-1 truncate font-mono text-xs text-muted">{referralLink}</span>
              </div>
            </div>
          </article>

          <article className="rounded-2xl border border-border bg-surface-elevated p-5 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-sm font-bold text-foreground">Your Commission Rates</h2>
              <FiDollarSign className="h-4 w-4 text-primary" />
            </div>
            <div className="mt-5 grid grid-cols-2 gap-3">
              <div className="rounded-xl border border-primary/20 bg-primary/10 p-4">
                <p className="text-xs text-muted">One-time</p>
                <p className="mt-2 text-2xl font-bold tabular-nums text-primary">
                  {referralsQuery.data?.oneTimeCommissionPercent ??
                    referralsQuery.data?.commissionPercent ??
                    0}
                  %
                </p>
              </div>
              <div className="rounded-xl border border-primary/20 bg-primary/10 p-4">
                <p className="text-xs text-muted">Recurring</p>
                <p className="mt-2 text-2xl font-bold tabular-nums text-primary">
                  {referralsQuery.data?.recurringCommissionPercent ??
                    referralsQuery.data?.commissionPercent ??
                    0}
                  %
                </p>
              </div>
            </div>
            <p className="mt-3 text-[11px] text-muted leading-relaxed">
              Commission = (payment − 3% fee) × your rate. Example: $100 at 10% → $9.70.
            </p>
            <div className="mt-4 space-y-3 text-xs">
              <div className="flex justify-between border-b border-border pb-3">
                <span className="text-muted">Network level</span>
                <span className="font-semibold text-foreground">Direct referrals</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted">Payout currency</span>
                <span className="font-semibold text-foreground">USD</span>
              </div>
            </div>
          </article>
        </section>
      </div>
    );
  }

  if (location.hash === '#affiliate-commissions') {
    return (
      <div className="animate-fadeIn space-y-5 pb-8">
        <header>
          <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-primary">
            <FiDollarSign className="h-3 w-3" />
            Commissions
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Commission History</h1>
          <p className="mt-1 text-sm text-muted">Review earnings generated from referral payments.</p>
        </header>
        {commissionHistorySection}
      </div>
    );
  }

  if (location.hash === '#affiliate-payouts') {
    return (
      <div className="animate-fadeIn space-y-5 pb-8">
        <header>
          <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-primary">
            <FiCreditCard className="h-3 w-3" />
            Payouts
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Partner Payouts</h1>
          <p className="mt-1 text-sm text-muted">Track pending earnings and payments approved by an administrator.</p>
        </header>

        <section id="affiliate-payouts" className="grid gap-4 sm:grid-cols-3">
          <article className="rounded-2xl border border-border bg-surface-elevated p-5 shadow-sm">
            <p className="text-xs text-muted">Available for Payout</p>
            <p className="mt-2 text-2xl font-bold tabular-nums text-foreground">{formatMoney(summary?.pendingCents || 0)}</p>
            {/* <button
              type="button"
              onClick={() => showToast('info', 'Payout requests are currently handled by an administrator.')}
              className="mt-4 rounded-lg bg-primary px-4 py-2.5 text-xs font-semibold text-white shadow-sm transition hover:brightness-110"
            >
              Request Payout
            </button> */}
            <button
              type="button"
              onClick={openPayoutDetails}
              className="mt-4 inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2.5 text-xs font-semibold text-foreground transition hover:border-primary hover:text-primary"
            >
              <FiCreditCard className="h-4 w-4" />
              {payoutDetailsQuery.data?.hasPayoutDetails ? 'Update Payout Details' : 'Add Payout Details'}
            </button>
          </article>
          <article className="rounded-2xl border border-border bg-surface-elevated p-5 shadow-sm">
            <p className="text-xs text-muted">Approved and Paid</p>
            <p className="mt-2 text-2xl font-bold tabular-nums text-primary">{formatMoney(summary?.withdrawnCents || 0)}</p>
            <p className="mt-4 text-xs text-muted">{payoutHistory.length} approved payout{payoutHistory.length === 1 ? '' : 's'}</p>
          </article>
          <article className="rounded-2xl border border-border bg-surface-elevated p-5 shadow-sm">
            <p className="text-xs text-muted">Lifetime Commission</p>
            <p className="mt-2 text-2xl font-bold tabular-nums text-foreground">{formatMoney(summary?.totalEarnedCents || 0)}</p>
            <p className="mt-4 text-xs text-muted">Pending and approved earnings</p>
          </article>
        </section>

        <section className="overflow-hidden rounded-2xl border border-border bg-surface-elevated shadow-sm">
          <div className="flex items-center justify-between gap-3 border-b border-border px-5 py-4">
            <div>
              <h2 className="text-base font-bold text-foreground">Payout History</h2>
              <p className="mt-1 text-xs text-muted">Commission batches that were approved and marked as paid.</p>
            </div>
            <span className="rounded-full bg-primary/10 px-3 py-1 text-[10px] font-bold text-primary">
              Approved only
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-left text-xs">
              <thead className="bg-surface text-muted">
                <tr>
                  <th className="px-5 py-3 font-semibold">Payout Date</th>
                  <th className="px-5 py-3 font-semibold">Referral Sources</th>
                  <th className="px-5 py-3 text-center font-semibold">Commissions</th>
                  <th className="px-5 py-3 text-right font-semibold">Amount</th>
                  <th className="px-5 py-3 text-right font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {payoutHistory.map((payout) => (
                  <tr key={payout.id} className="border-t border-border hover:bg-surface/70">
                    <td className="px-5 py-4 text-foreground">
                      {new Date(payout.paidAt).toLocaleDateString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </td>
                    <td className="max-w-[280px] truncate px-5 py-4 text-muted" title={payout.referralSummary}>
                      {payout.referralSummary || 'Referral commissions'}
                    </td>
                    <td className="px-5 py-4 text-center tabular-nums text-muted">{payout.commissionCount}</td>
                    <td className="px-5 py-4 text-right font-bold tabular-nums text-foreground">
                      {formatMoney(payout.amountCents, payout.currency)}
                    </td>
                    <td className="px-5 py-4 text-right">
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-1 font-semibold text-primary">
                        <FiCheck className="h-3 w-3" />
                        Approved
                      </span>
                    </td>
                  </tr>
                ))}
                {payoutHistory.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-5 py-14 text-center text-muted">
                      No approved payouts yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
        {payoutDetailsModal}
      </div>
    );
  }

  if (location.hash === '#affiliate-statistics') {
    return (
      <div className="animate-fadeIn space-y-5 pb-8">
        <header>
          <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-primary">
            <FiTrendingUp className="h-3 w-3" />
            Statistics
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Affiliate Statistics</h1>
          <p className="mt-1 text-sm text-muted">See your top referrals, generated business, and network growth.</p>
        </header>

        <section id="affiliate-statistics" className="grid gap-4 sm:grid-cols-3">
          <article className="rounded-2xl border border-border bg-surface-elevated p-5 shadow-sm">
            <p className="text-xs text-muted">Total Referrals</p>
            <p className="mt-2 text-2xl font-bold tabular-nums text-foreground">{referrals.length}</p>
          </article>
          <article className="rounded-2xl border border-border bg-surface-elevated p-5 shadow-sm">
            <p className="text-xs text-muted">Business Generated</p>
            <p className="mt-2 text-2xl font-bold tabular-nums text-foreground">{formatMoney(totalBusinessCents)}</p>
          </article>
          <article className="rounded-2xl border border-border bg-surface-elevated p-5 shadow-sm">
            <p className="text-xs text-muted">Lifetime Commission</p>
            <p className="mt-2 text-2xl font-bold tabular-nums text-primary">{formatMoney(summary?.totalEarnedCents || 0)}</p>
          </article>
        </section>

        <section className="grid gap-5 2xl:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
          <article className="overflow-hidden rounded-2xl border border-border bg-surface-elevated shadow-sm">
            <div className="flex flex-col gap-3 border-b border-border px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-base font-bold text-foreground">Top Referrals</h2>
                  <span className="rounded-full border border-border px-2.5 py-1 text-[10px] font-semibold text-muted">
                    {referrals.length} total
                  </span>
                </div>
                <p className="mt-1 text-xs text-muted">
                  Commission uses your one-time / recurring rates on (payment − 3%).
                  Currently {referralsQuery.data?.oneTimeCommissionPercent ?? referralsQuery.data?.commissionPercent ?? 0}% one-time
                  and {referralsQuery.data?.recurringCommissionPercent ?? referralsQuery.data?.commissionPercent ?? 0}% recurring.
                </p>
              </div>
              <div className="text-left sm:text-right">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted">Lifetime</p>
                <p className="mt-1 text-xl font-bold tabular-nums text-foreground">{formatMoney(summary?.totalEarnedCents || 0)}</p>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[620px] text-left text-xs">
                <thead className="bg-surface text-muted">
                  <tr>
                    <th className="px-5 py-3 font-semibold">#</th>
                    <th className="px-5 py-3 font-semibold">Referral</th>
                    <th className="px-5 py-3 font-semibold">Level</th>
                    <th className="px-5 py-3 text-right font-semibold">Business</th>
                    <th className="px-5 py-3 text-right font-semibold">Commission</th>
                  </tr>
                </thead>
                <tbody>
                  {topReferrals.map((referral, index) => (
                    <tr key={referral.name} className="border-t border-border hover:bg-surface/70">
                      <td className="px-5 py-4 text-muted">{index + 1}</td>
                      <td className="px-5 py-4 font-semibold text-foreground">{referral.name}</td>
                      <td className="px-5 py-4 text-muted">L1</td>
                      <td className="px-5 py-4 text-right tabular-nums text-muted">{formatMoney(referral.businessCents)}</td>
                      <td className="px-5 py-4 text-right font-bold tabular-nums text-primary">{formatMoney(referral.commissionCents)}</td>
                    </tr>
                  ))}
                  {topReferrals.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-5 py-12 text-center text-muted">No referral earnings yet.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </article>

          <article className="rounded-2xl border border-border bg-surface-elevated p-5 shadow-sm">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-base font-bold text-foreground">Referral Growth</h2>
                <p className="mt-1 text-xs text-muted">Cumulative referral signups</p>
              </div>
              <div className="flex gap-1">
                {(Object.keys(growthPeriodDays) as GrowthPeriod[]).map((item) => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => setGrowthPeriod(item)}
                    className={`rounded-lg px-3 py-2 text-[10px] font-semibold transition ${
                      growthPeriod === item ? 'bg-primary text-white shadow-sm' : 'text-muted hover:bg-surface'
                    }`}
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-5 h-[390px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={referralGrowthData} margin={{ top: 12, right: 8, left: -18, bottom: 0 }}>
                  <defs>
                    <linearGradient id="referralGrowthFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} />
                  <YAxis allowDecimals={false} axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} />
                  <Tooltip
                    formatter={(value: number) => [value, 'Referrals']}
                    contentStyle={{
                      borderRadius: 12,
                      border: '1px solid hsl(var(--border))',
                      background: 'hsl(var(--surface-elevated))',
                      fontSize: 12,
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="referrals"
                    stroke="#2563eb"
                    strokeWidth={2.5}
                    fill="url(#referralGrowthFill)"
                    activeDot={{ r: 5, fill: '#2563eb', stroke: '#fff', strokeWidth: 2 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </article>
        </section>
      </div>
    );
  }

  return (
    <div id="affiliate-dashboard" className="animate-fadeIn scroll-mt-24 space-y-5 ">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-primary">
            <FiGitBranch className="h-3 w-3" />
            Partner program
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Affiliate Dashboard</h1>
          <p className="mt-1 text-sm text-muted">Track referrals, commissions, and partner performance.</p>
        </div>
        {/* <div className="flex items-center gap-2 text-xs text-muted">
          <span className="h-2 w-2 rounded-full bg-primary shadow-[0_0_0_4px_rgba(37,99,235,0.12)]" />
          Live data
        </div> */}
      </header>

      <section id="affiliate-statistics" className="scroll-mt-24 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {displayedStats.map((stat) => {
          const Icon = stat.icon;
          return (
            <article
              key={stat.label}
              className="group relative overflow-hidden rounded-2xl border border-border bg-surface-elevated p-5 shadow-sm transition duration-200 hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-lg"
            >
              <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-transparent via-primary/60 to-transparent opacity-0 transition group-hover:opacity-100" />
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-medium text-muted">{stat.label}</p>
                  <p className="mt-2 text-xl font-bold tabular-nums text-foreground">{stat.value}</p>
                </div>
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Icon className="h-4 w-4" />
                </div>
              </div>
            </article>
          );
        })}
      </section>

      <section className="grid gap-5 xl:grid-cols-[minmax(0,2fr)_minmax(280px,1fr)]">
        <article className="min-h-[470px] rounded-2xl border border-border bg-surface-elevated p-5 shadow-sm">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-base font-bold text-foreground">Performance</h2>
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="rounded-full border border-border px-2.5 py-1 text-[10px] font-semibold text-foreground">
                  {summary?.referralCount ?? referrals.length} Referrals
                </span>
                <span className="rounded-full border border-border px-2.5 py-1 text-[10px] font-semibold text-foreground">
                  {formatMoney(summary?.totalEarnedCents || 0)} Commissions
                </span>
                <span className="rounded-full border border-border px-2.5 py-1 text-[10px] font-semibold text-foreground">
                  {formatMoney(totalBusinessCents)} Business
                </span>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {(Object.keys(periodDays) as Period[]).map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setPeriod(item)}
                  className={`rounded-lg px-3 py-2 text-[10px] font-semibold transition ${
                    period === item
                      ? 'bg-primary text-white shadow-sm'
                      : 'text-muted hover:bg-surface'
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-5 h-[330px] w-full">
            {referralsQuery.isLoading ? (
              <div className="flex h-full items-center justify-center text-sm text-muted">Loading performance…</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={performanceData} barCategoryGap="28%">
                  <CartesianGrid vertical={false} stroke="hsl(var(--border))" strokeDasharray="3 3" />
                  <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} />
                  <YAxis axisLine={false} tickLine={false} width={44} tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} />
                  <Tooltip
                    formatter={(value: number, name: string) => [chartMoney(value), name === 'business' ? 'Business' : 'Commission']}
                    contentStyle={{
                      borderRadius: 12,
                      border: '1px solid hsl(var(--border))',
                      background: 'hsl(var(--surface-elevated))',
                      fontSize: 12,
                    }}
                  />
                  <Bar dataKey="business" stackId="performance" fill="#60a5fa" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="commission" stackId="performance" fill="#2563eb" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </article>

        <div className="space-y-5">
          <article id="affiliate-payouts" className="rounded-2xl border border-border bg-surface-elevated p-5 shadow-sm">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <FiCreditCard className="h-4 w-4" />
                </div>
                <h2 className="text-sm font-bold text-foreground">Partner Wallet</h2>
              </div>
              <span className="text-xs font-medium tabular-nums text-muted">
                {formatMoney(summary?.pendingCents || 0)}
              </span>
            </div>
            {/* <button
              type="button"
              onClick={() => showToast('info', 'Payout requests are currently handled by an administrator.')}
              className="mt-5 w-full rounded-lg border border-border px-4 py-3 text-xs font-semibold text-foreground transition hover:border-primary hover:text-primary"
            >
              Request Payout
            </button> */}
            <button
              type="button"
              onClick={openPayoutDetails}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-3 text-xs font-semibold text-white shadow-sm transition hover:brightness-110"
            >
              <FiCreditCard className="h-4 w-4" />
              {payoutDetailsQuery.data?.hasPayoutDetails ? 'Update Payout Details' : 'Add Payout Details'}
            </button>
          </article>

          <article id="affiliate-links" className="rounded-2xl border border-border bg-surface-elevated p-5 shadow-sm">
            <h2 className="text-base font-bold text-foreground">Referral Link</h2>
            <div className="mt-5 rounded-xl border border-border bg-surface p-3">
              <div className="flex items-center justify-between gap-3">
                <span className="rounded-full bg-primary/10 px-2.5 py-1 text-[10px] font-bold text-primary">
                  {referralCode}
                </span>
                <div className="flex items-center gap-1">
                  <button type="button" onClick={shareReferralLink} className="rounded-md p-2 text-muted hover:bg-surface-elevated hover:text-foreground" aria-label="Share referral link">
                    <FiShare2 className="h-4 w-4" />
                  </button>
                  <button type="button" onClick={copyReferralLink} className="rounded-md p-2 text-muted hover:bg-surface-elevated hover:text-foreground" aria-label="Copy referral link">
                    {copied ? <FiCheck className="h-4 w-4 text-primary" /> : <FiCopy className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="mt-2 flex items-center gap-2 rounded-lg bg-surface-elevated px-3 py-2.5">
                <FiLink className="h-3.5 w-3.5 shrink-0 text-muted" />
                <span className="min-w-0 flex-1 truncate font-mono text-[10px] text-muted">{referralLink}</span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowRates((current) => !current)}
              className="mt-3 w-full rounded-lg border border-border px-4 py-3 text-[11px] font-semibold text-foreground transition hover:bg-surface"
            >
              {showRates ? 'Hide Commission Rates' : 'View Commission Rates'}
            </button>
            {showRates && (
              <div className="mt-3 rounded-lg bg-primary/10 px-3 py-2 text-center text-xs font-semibold text-primary">
                Earn{' '}
                {referralsQuery.data?.oneTimeCommissionPercent ??
                  referralsQuery.data?.commissionPercent ??
                  0}
                % one-time /{' '}
                {referralsQuery.data?.recurringCommissionPercent ??
                  referralsQuery.data?.commissionPercent ??
                  0}
                % recurring on eligible referral payments (after 3% fee).
              </div>
            )}
          </article>
        </div>
      </section>

      {/* {referralTreeSection}

      {commissionHistorySection} */}
      {payoutDetailsModal}
    </div>
  );
};

export default AffiliateProgram;
